/**
 * MOB-1 · Matemática del joystick virtual. Pura y sin DOM.
 *
 * Separada del cableado de eventos a propósito: lo que decide si el juego se
 * siente bien con el pulgar no son los `PointerEvent`, es esto — dónde empieza
 * a responder (zona muerta), cómo se reparten las ocho direcciones y qué pasa
 * cuando el dedo se sale del círculo. Aquí se puede probar sin navegador.
 *
 * El vector del pulgar se entrega al sim de DOS formas: como eje analógico
 * (`mx`/`my`, que es el que manda: medio stick = media fuerza) y discretizado a
 * las cuatro teclas, que se conservan por compatibilidad. Antes solo existía lo
 * segundo, y por eso daba igual rozar el stick que estrujarlo: el disco salía
 * disparado a tope en las ocho direcciones y colocarse era imposible.
 */

/** Lectura normalizada del joystick. `x`/`y` van en [-1, 1] (y+ hacia abajo,
 *  como el canvas). `magnitude` es 0 en el centro y 1 en el borde. */
export interface JoystickReading {
  x: number;
  y: number;
  magnitude: number;
  /** false mientras el dedo esté dentro de la zona muerta: no mueve nada. */
  active: boolean;
}

/** Direcciones cardinales que consume `InputState`. */
export interface Directions {
  up: boolean;
  down: boolean;
  left: boolean;
  right: boolean;
}

/**
 * Fracción del radio por debajo de la cual el joystick no responde.
 *
 * Sin zona muerta el disco deriva solo: un pulgar apoyado nunca está exactamente
 * en el centro, y el pulso basta para que el juego lea "izquierda" un instante y
 * "derecha" al siguiente. 0.22 es suficiente para que quieto sea quieto sin que
 * haya que exagerar el gesto para arrancar.
 */
export const DEAD_ZONE = 0.22;

/**
 * Umbral por eje para las ocho direcciones: `sin(22.5°)`.
 *
 * Es el valor que parte el círculo en OCHO SECTORES IGUALES de 45°. Cualquier
 * otro número deforma el mando: más alto y las diagonales desaparecen (hay que
 * apuntar clavado a 45° para conseguirlas), más bajo y no se puede ir recto —
 * el menor desvío al empujar hacia arriba te mete además una lateral.
 */
export const DIAGONAL_THRESHOLD = Math.sin(Math.PI / 8);

/**
 * Convierte la posición del dedo en una lectura normalizada respecto del origen
 * del joystick.
 *
 * El vector se ACOTA al radio en vez de escalarse: pasado el borde, seguir
 * estirando el dedo no hace nada más, que es justo lo que espera quien lo usa.
 * `radius` no negativo se trata como centro exacto para no dividir por cero.
 */
export function readJoystick(
  originX: number,
  originY: number,
  pointerX: number,
  pointerY: number,
  radius: number,
  deadZone: number = DEAD_ZONE,
): JoystickReading {
  if (radius <= 0) return { x: 0, y: 0, magnitude: 0, active: false };
  const dx = pointerX - originX;
  const dy = pointerY - originY;
  const dist = Math.hypot(dx, dy);
  if (dist === 0) return { x: 0, y: 0, magnitude: 0, active: false };
  const magnitude = Math.min(1, dist / radius);
  // Dirección unitaria (independiente de lo lejos que esté el dedo).
  const ux = dx / dist;
  const uy = dy / dist;
  return {
    x: ux * magnitude,
    y: uy * magnitude,
    magnitude,
    active: magnitude >= deadZone,
  };
}

/**
 * Eje analógico del `InputState` (centésimas, -100..100) para una lectura.
 *
 * La magnitud se REMAPEA desde la zona muerta: justo al salir de ella el disco
 * arranca despacio (y no de golpe al 22%), y en el borde va a tope. Sin ese
 * remapeo, el primer tramo útil del stick daría un salto de velocidad y el
 * control fino —que es justo lo que se busca— no existiría.
 *
 * Se devuelven ENTEROS porque esto viaja por la red y lo simulan las dos
 * puntas: ver `InputState.mx`.
 */
export function joystickToAxis(
  r: JoystickReading,
  scale = 100,
  deadZone: number = DEAD_ZONE,
): { mx: number; my: number } {
  if (!r.active || r.magnitude === 0) return { mx: 0, my: 0 };
  const span = 1 - deadZone;
  const lin = span > 0 ? Math.min(1, (r.magnitude - deadZone) / span) : 1;
  // Curva de respuesta: el primer tramo del stick vale MENOS que lineal, así
  // que colocarse fino es posible y el disco no sale disparado al primer roce.
  // El fondo del recorrido sigue dando el 100%, que es lo que se espera al
  // estirar el pulgar del todo.
  const m = Math.pow(lin, RESPONSE_CURVE);
  const ux = r.x / r.magnitude;
  const uy = r.y / r.magnitude;
  return { mx: Math.round(ux * m * scale), my: Math.round(uy * m * scale) };
}

/**
 * Exponente de la curva de respuesta del stick (1 = lineal).
 *
 * 1.5 es el compromiso habitual de un mando analógico: la mitad del recorrido
 * da un tercio de la fuerza (control fino donde se necesita) y el último tramo
 * sube rápido hasta el tope. Con respuesta lineal, cualquier temblor del pulgar
 * ya movía el disco medio campo — que es exactamente lo que se describía como
 * "controles bruscos".
 */
export const RESPONSE_CURVE = 1.5;

/**
 * Suavizado del eje entre lecturas (0 = sin suavizado, 1 = congelado).
 *
 * Un dedo sobre cristal no es un potenciómetro: la posición que reporta el
 * navegador salta unos píxeles entre eventos, y esos saltos llegaban tal cual al
 * disco. Este filtro promedia la orden con la anterior — lo justo para que el
 * ruido desaparezca sin que el mando se sienta "con retraso".
 */
export const AXIS_SMOOTHING = 0.45;

/**
 * Mezcla el eje nuevo con el anterior (filtro paso bajo de primer orden).
 *
 * Se corta en seco al SOLTAR: si el suavizado también arrastrara la vuelta a
 * cero, el disco seguiría corriendo un instante después de levantar el pulgar, y
 * eso no es suavidad, es no obedecer.
 */
export function smoothAxis(
  prev: { mx: number; my: number },
  next: { mx: number; my: number },
  k: number = AXIS_SMOOTHING,
): { mx: number; my: number } {
  if (next.mx === 0 && next.my === 0) return { mx: 0, my: 0 };
  return {
    mx: Math.round(prev.mx + (next.mx - prev.mx) * (1 - k)),
    my: Math.round(prev.my + (next.my - prev.my) * (1 - k)),
  };
}

/**
 * Discretiza la lectura a las cuatro teclas del `InputState` (8 direcciones).
 *
 * Sigue existiendo junto al eje analógico: son la MISMA orden en dos formatos.
 * El eje es el que mueve el disco; los booleanos mantienen coherente el resto
 * del sistema (un servidor antiguo que solo entienda teclas, y cualquier código
 * que pregunte "¿va a la izquierda?").
 *
 * Se comparan las componentes UNITARIAS, no las escaladas por la magnitud: si
 * no, la dirección elegida dependería de cuánto se ha estirado el dedo, y un
 * empujón suave en diagonal se leería como cardinal.
 */
export function joystickToDirections(
  r: JoystickReading,
  threshold: number = DIAGONAL_THRESHOLD,
): Directions {
  if (!r.active || r.magnitude === 0) {
    return { up: false, down: false, left: false, right: false };
  }
  const ux = r.x / r.magnitude;
  const uy = r.y / r.magnitude;
  return {
    up: uy < -threshold,
    down: uy > threshold,
    left: ux < -threshold,
    right: ux > threshold,
  };
}

/**
 * Posición del pomo dentro de la base, en píxeles relativos al origen. Es solo
 * presentación, pero vive aquí porque debe usar exactamente el mismo acotado
 * que la lectura: un pomo que se dibuja fuera del círculo del que el juego ya
 * no lee nada más es una promesa que el control no cumple.
 */
export function knobOffset(r: JoystickReading, radius: number): { x: number; y: number } {
  return { x: r.x * radius, y: r.y * radius };
}
